import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import StatusBadge from "../components/StatusBadge";
import { gbp, dateShort, emptyDraftDeleteDate } from "../lib/format";

const ADMIN_SWA_URL =
  (import.meta.env.VITE_ADMIN_SWA_URL as string | undefined) ??
  "https://expenses-admin.liaisegroup.com";

export default function LandingPage() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const { data, isLoading, error } = useQuery({ queryKey: ["expenses"], queryFn: () => api.expenses.list() });

  const isApprover = me?.is_approver ?? false;
  const pending = Number(me?.pending_approvals ?? 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">My expenses</h1>
          <p className="text-slate-600 mt-1">Submit a claim, track approvals, and see what's been paid.</p>
        </div>
        <Link
          to="/new"
          className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 transition"
        >
          New claim
        </Link>
      </div>

      {me && (
        <div className="text-sm text-slate-500">
          Signed in as <span className="font-medium text-slate-700">{me.name}</span>
          {me.manager && <> · Manager: <span className="text-slate-700">{me.manager.displayName}</span></>}
        </div>
      )}

      {isApprover && (
        <a
          href={`${ADMIN_SWA_URL}/approvals`}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-liaise-navy text-white rounded-xl p-5 shadow-sm hover:bg-liaise-navy-700 transition"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium flex items-center gap-2">
                You're a manager
                {pending > 0 && (
                  <span className="bg-liaise-yellow text-liaise-navy text-xs font-semibold px-2 py-0.5 rounded-full">
                    {pending} pending
                  </span>
                )}
              </div>
              <div className="text-white/80 text-sm mt-1">
                {pending > 0
                  ? `${pending} claim${pending === 1 ? "" : "s"} from your team need${pending === 1 ? "s" : ""} your action.`
                  : "Open the approvals app to review your team's history."}
              </div>
            </div>
            <span className="text-white/80 text-sm">Open approvals →</span>
          </div>
        </a>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-medium text-liaise-navy">Recent claims</h2>
        </div>
        {isLoading && <div className="px-6 py-12 text-slate-500 text-center">Loading…</div>}
        {error && <div className="px-6 py-12 text-red-600 text-sm">{(error as Error).message}</div>}
        {data && data.items.length === 0 && (
          <div className="px-6 py-12 text-center text-slate-500">
            No claims yet. <Link to="/new" className="text-liaise-navy underline">Start your first one</Link>.
          </div>
        )}
        {data && data.items.length > 0 && (
          <>
            {/* Phone: stacked cards so nothing is clipped off the right edge */}
            <ul className="sm:hidden divide-y divide-slate-100">
              {data.items.map((c) => (
                <li key={c.id}>
                  <Link to={`/claim/${c.id}`} className="block px-5 py-4 hover:bg-slate-50">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-liaise-navy font-semibold">{c.request_number}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-600 truncate">{dateShort(c.created_at)} · {c.cost_centre_name}</span>
                      <span className="font-semibold text-slate-800 whitespace-nowrap">{gbp(Number(c.total_amount))}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-400">{c.line_count} line{c.line_count === 1 ? "" : "s"}</div>
                    {c.status === "draft" && c.line_count === 0 && (
                      <div className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                        Empty draft. Add a line or it will be deleted automatically on {emptyDraftDeleteDate(c.created_at)}.
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Tablet and desktop: full table */}
            <table className="hidden sm:table w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Number</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium">Cost centre</th>
                <th className="px-6 py-3 font-medium">Lines</th>
                <th className="px-6 py-3 font-medium text-right">Total</th>
                <th className="px-6 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-6 py-3">
                    <Link to={`/claim/${c.id}`} className="text-liaise-navy font-medium hover:underline">
                      {c.request_number}
                    </Link>
                  </td>
                  <td className="px-6 py-3 text-slate-600">{dateShort(c.created_at)}</td>
                  <td className="px-6 py-3 text-slate-600">{c.cost_centre_name}</td>
                  <td className="px-6 py-3 text-slate-600">
                    {c.line_count}
                    {c.status === "draft" && c.line_count === 0 && (
                      <span className="ml-2 inline-block text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5">
                        empty, deleted {emptyDraftDeleteDate(c.created_at)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right font-medium">{gbp(Number(c.total_amount))}</td>
                  <td className="px-6 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
